import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { loadChartRuntime, type ChartRuntime } from './loader';
import type { ChartInput } from './types';
import { splitDomMetadata, type DomMetadata } from '../../dom-metadata';

export function EnhancedWorkspaceChart({
  table,
  ...props
}: ChartInput & { table: ReactNode } & DomMetadata) {
  const { metadata, rest: input } = splitDomMetadata(props);
  const host = useRef<HTMLDivElement>(null),
    latest = useRef(input);
  const mounted = useRef<ReturnType<ChartRuntime['mountChart']> | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [tableOpen, setTableOpen] = useState(false);
  const id = useId(),
    e = input.enhanced;
  latest.current = input;
  useEffect(() => {
    let active = true;
    loadChartRuntime()
      .then((runtime) => {
        if (!active || !host.current) return;
        mounted.current = runtime.mountChart(host.current, latest.current);
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('failed');
      });
    return () => {
      active = false;
      mounted.current?.dispose();
      mounted.current = null;
    };
  }, []);
  useEffect(() => {
    try {
      mounted.current?.update(input);
    } catch {
      mounted.current?.dispose();
      mounted.current = null;
      setStatus('failed');
    }
  }, [input.chart, input.columns, input.enhanced]);
  const showTable = status === 'failed' || (e.tableVisible ?? tableOpen);
  return (
    <div className="ws-chart" {...metadata}>
      {status === 'failed' && (
        <p className="ws-muted" role="status">
          {e.labels.unavailable}
        </p>
      )}
      <div className="ws-chart-stage">
        {/* Loading is a skeleton block the size of the plot, never a line of text (§5.1). */}
        {status === 'loading' && !showTable && (
          <div className="ws-chart-skeleton" role="status">
            <span className="pc-sr-only">{e.labels.loading}</span>
          </div>
        )}
        <div
          ref={host}
          role="img"
          aria-label={e.summary}
          aria-describedby={id}
          tabIndex={0}
          aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Enter F"
          style={{
            width: '100%',
            height: e.height ?? 200,
            display: showTable ? 'none' : undefined,
          }}
        />
      </div>
      {/* The caption sits right under the axis: the summary, then the table toggle after " · ". */}
      <p className="ws-chart-caption">
        <span id={id} className="ws-chart-summary">
          {e.summary}
        </span>
        {status !== 'failed' && e.tableVisible === undefined && (
          <>
            <span className="ws-chart-sep" aria-hidden="true">
              {' · '}
            </span>
            <button
              type="button"
              className="pc-link"
              aria-expanded={showTable}
              aria-controls={`${id}-table`}
              onClick={() => setTableOpen((open) => !open)}
            >
              {showTable ? e.labels.hideTable : e.labels.showTable}
            </button>
          </>
        )}
      </p>
      <div id={`${id}-table`} hidden={!showTable}>
        {table}
      </div>
    </div>
  );
}
